import { useState, useEffect } from "react";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { AvatarBlock } from "@/components/settings/AccountSection";
import StatusBadge from "@/components/tasks/StatusBadge";
import PriorityBadge from "@/components/tasks/PriorityBadge";
import { Calendar, MessageCircle, Paperclip, CheckSquare, Users, FileText, Clock, Eye } from "lucide-react";
import api from "@/lib/api";

/**
 * LinkedTaskBadge — spec rev148 (2026-02-06).
 *
 * Petit badge cliquable placé à côté de la référence document, portant
 * la référence de la tâche liée (fond #332612, texte #F9BC26, souligné).
 * Au clic → navigation directe vers la fiche tâche complète.
 * Au survol → popover d'aperçu détaillé qui reprend les sections
 * principales de la fiche tâche complète : titre, statut, infos
 * (assignés/priorité/deadline), description, checklist, pièces jointes,
 * discussion. Fetch on demand (lazy) pour éviter de charger toutes les
 * fiches liées à l'ouverture du document.
 */
export default function LinkedTaskBadge({ task, users = [], usersById = {}, onOpen }) {
  const [open, setOpen] = useState(false);
  const [full, setFull] = useState(null);
  const [loading, setLoading] = useState(false);

  // Fetch full task detail on first hover.
  useEffect(() => {
    if (!open || full) return;
    setLoading(true);
    api.get(`/tasks/${task.id}`)
      .then((r) => setFull(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [open, task?.id, full]);

  const data = full || task;
  const assignees = (data.assignees || data.assignee_ids || []).map((a) =>
    typeof a === "string" ? usersById[a] : a
  ).filter(Boolean);
  const readBy = data.read_by || {};
  const creator = usersById[data.created_by];

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <span
          onMouseEnter={() => setOpen(true)}
          onMouseLeave={() => setTimeout(() => setOpen(false), 100)}
          onClick={(e) => { e.stopPropagation(); onOpen?.(); }}
          className="inline-flex items-center gap-1 text-[10.5px] font-semibold px-1.5 py-0.5 rounded cursor-pointer underline underline-offset-2 hover:brightness-125 transition"
          style={{ background: "#332612", color: "#F9BC26" }}
          data-testid={`linked-task-badge-${task.id}`}
          title={`Tâche ${task.reference || task.id} — cliquer pour ouvrir`}
        >
          <Clock size={10} strokeWidth={2.6} /> {task.reference || task.id}
        </span>
      </PopoverTrigger>
      <PopoverContent
        side="bottom" align="start"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        className="p-0 w-[560px] max-h-[80vh] overflow-y-auto"
        style={{ background: "#141414", border: "1px solid #2A2A2A" }}
        data-testid={`linked-task-popover-${task.id}`}
      >
        <div className="p-4 space-y-3">
          {/* Header : réf + statut + priorité */}
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className="inline-block text-[11px] font-bold px-2 py-0.5 rounded"
              style={{ background: "#F9BC26", color: "#0B0B0B" }}
            >{data.reference || task.reference || task.id}</span>
            <StatusBadge status={data.status} />
            {data.priority && <PriorityBadge priority={data.priority} />}
          </div>
          <h3 className="text-[14px] font-bold leading-tight" style={{ color: "#EDEDED" }}>
            {data.title || "(sans titre)"}
          </h3>

          {/* Infos */}
          <div className="grid grid-cols-2 gap-2 text-[11.5px]">
            {data.due_date && (
              <div className="flex items-center gap-1.5" style={{ color: "#9CA3AF" }}>
                <Calendar size={11} /> Échéance {new Date(data.due_date).toLocaleDateString("fr-FR")}
              </div>
            )}
            {creator && (
              <div className="flex items-center gap-1.5" style={{ color: "#9CA3AF" }}>
                <FileText size={11} /> Créée par {creator.first_name} {creator.last_name}
              </div>
            )}
          </div>

          {/* Assignés (avec vu/non vu) */}
          {assignees.length > 0 && (
            <div>
              <div className="text-[9.5px] uppercase tracking-widest mb-1.5" style={{ color: "#6B7280" }}>
                <Users size={10} className="inline mr-1" /> Assignés ({assignees.length})
              </div>
              <div className="flex flex-wrap gap-1.5">
                {assignees.map((u) => {
                  const isRead = !!(readBy && readBy[u.id]);
                  return (
                    <div
                      key={u.id}
                      className="inline-flex items-center gap-1 pl-0.5 pr-1.5 py-0.5 rounded-full text-[10.5px]"
                      style={{
                        background: isRead ? "rgba(34,197,94,0.10)" : "rgba(239,68,68,0.10)",
                        border: `1px solid ${isRead ? "rgba(34,197,94,0.35)" : "rgba(239,68,68,0.35)"}`,
                        color: isRead ? "#4ADE80" : "#FCA5A5",
                      }}
                      title={isRead ? "Vue" : "Non vue"}
                    >
                      <AvatarBlock user={u} size={16} />
                      <span>{u.first_name}</span>
                      {isRead
                        ? <Eye size={9} strokeWidth={2.6} />
                        : <span className="text-[9px] opacity-70">·</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Description */}
          {data.description && (
            <div>
              <div className="text-[9.5px] uppercase tracking-widest mb-1" style={{ color: "#6B7280" }}>
                Description
              </div>
              <div className="text-[12px] whitespace-pre-wrap leading-relaxed" style={{ color: "#D1D5DB" }}>
                {data.description.length > 400 ? data.description.slice(0, 400) + "…" : data.description}
              </div>
            </div>
          )}

          {/* Checklist */}
          {(data.checklist || []).length > 0 && (
            <div>
              <div className="text-[9.5px] uppercase tracking-widest mb-1.5 flex items-center gap-1" style={{ color: "#6B7280" }}>
                <CheckSquare size={10} /> Checklist ({(data.checklist || []).filter((i) => i.done).length}/{(data.checklist || []).length})
              </div>
              <div className="space-y-1 max-h-[100px] overflow-y-auto">
                {(data.checklist || []).slice(0, 8).map((item, idx) => (
                  <div key={idx} className="flex items-center gap-1.5 text-[11.5px]" style={{ color: item.done ? "#6B7280" : "#EDEDED" }}>
                    <span style={{ color: item.done ? "#4ADE80" : "#6B7280" }}>{item.done ? "☑" : "☐"}</span>
                    <span className={item.done ? "line-through" : ""}>{item.label || item.text || "(item)"}</span>
                  </div>
                ))}
                {(data.checklist || []).length > 8 && (
                  <div className="text-[10px] italic" style={{ color: "#6B7280" }}>
                    +{(data.checklist || []).length - 8} autres items…
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Pièces jointes */}
          {(data.attachments || []).length > 0 && (
            <div>
              <div className="text-[9.5px] uppercase tracking-widest mb-1.5 flex items-center gap-1" style={{ color: "#6B7280" }}>
                <Paperclip size={10} /> Pièces jointes ({(data.attachments || []).length})
              </div>
              <div className="flex flex-wrap gap-1.5">
                {(data.attachments || []).slice(0, 6).map((a, i) => (
                  <div
                    key={i}
                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10.5px]"
                    style={{ background: "rgba(255,255,255,0.04)", color: "#9CA3AF", border: "1px solid rgba(255,255,255,0.08)" }}
                    title={a.original_filename || a.name}
                  >
                    <Paperclip size={9} />
                    {(a.original_filename || a.name || "fichier").slice(0, 24)}
                  </div>
                ))}
                {(data.attachments || []).length > 6 && (
                  <span className="text-[10px] italic" style={{ color: "#6B7280" }}>+{(data.attachments || []).length - 6}</span>
                )}
              </div>
            </div>
          )}

          {/* Discussion — derniers messages */}
          {(data.messages || []).length > 0 && (
            <div>
              <div className="text-[9.5px] uppercase tracking-widest mb-1.5 flex items-center gap-1" style={{ color: "#6B7280" }}>
                <MessageCircle size={10} /> Discussion ({(data.messages || []).length})
              </div>
              <div className="space-y-1.5 max-h-[140px] overflow-y-auto">
                {(data.messages || []).slice(-4).map((m, idx) => {
                  const author = usersById[m.author_id];
                  return (
                    <div key={idx} className="flex items-start gap-1.5">
                      {author && <AvatarBlock user={author} size={18} />}
                      <div className="flex-1 min-w-0">
                        <div className="text-[10px]" style={{ color: "#9CA3AF" }}>
                          {author ? `${author.first_name} ${author.last_name}` : "—"}
                        </div>
                        <div className="text-[11.5px] leading-snug" style={{ color: "#D1D5DB" }}>
                          {(m.body || m.text || "").slice(0, 180)}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {loading && (
            <div className="text-[11px] italic" style={{ color: "#6B7280" }}>
              Chargement de la fiche complète…
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
